import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  InputAdornment,
  Grid,
  FormHelperText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
  Fade,
  Backdrop
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Lock as LockIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ZoomIn as ZoomInIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { registerDriver } from '../driversApi';
import { fetchCities } from '../citiesApi';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';

const RegisterDriver = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    FullName: '',
    MobileNumber: '',
    Email: '',
    City: '',
    Password: ''
  });
  const { selectedBranch } = useBranch();
  const [helpOpen, setHelpOpen] = useState(false);

  // ✅ Image Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState('');
  
  const sliderImages = [
    '/register-driver1.png',
    '/register-driver2.png'
  ];

  useEffect(() => {
    loadCities();
  }, []);

  // ✅ Auto-play slider
  useEffect(() => {
    if (!zoomOpen) { // Pause autoplay when zoom is open
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [sliderImages.length, zoomOpen]);

  const loadCities = async () => {
    try {
      const data = await fetchCities();
      setCities(data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'MobileNumber') {
      const val = value.replace(/[^0-9]/g, '').slice(0, 9);
      setFormData({ ...formData, [name]: val });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      Swal.fire(t('warning'), t('pleaseSelectBranchFirst'), 'warning');
      return;
    }
    if (!formData.FullName || !formData.MobileNumber || !formData.City || !formData.Password) {
      Swal.fire(t('error'), t('fillAllRequiredFields'), 'error');
      return;
    }

    if (formData.MobileNumber.length !== 9) {
      Swal.fire(t('error'), t('mobileNumberLengthError'), 'error');
      return;
    }

    try {
      const payload = {
        ...formData,
        MobileNumber: '+967' + formData.MobileNumber,
        BranchId: selectedBranch.id || selectedBranch.Id
      };
      await registerDriver(payload);
      Swal.fire(t('success'), t('driverRegisteredSuccess'), 'success');
      navigate('/admin/drivers');
    } catch (error) {
      Swal.fire(t('error'), t('failedToRegisterDriver'), 'error');
    }
  };

  const handleReset = () => {
    setFormData({
      FullName: '',
      MobileNumber: '',
      Email: '',
      City: '',
      Password: ''
    });
  };

  // ✅ Slider Navigation
  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  // ✅ Zoom Handlers
  const handleZoomOpen = (image) => {
    setZoomedImage(image);
    setZoomOpen(true);
  };

  const handleZoomClose = () => {
    setZoomOpen(false);
  };

  const handleZoomPrev = (e) => {
    e.stopPropagation();
    const currentIndex = sliderImages.indexOf(zoomedImage);
    const prevIndex = (currentIndex - 1 + sliderImages.length) % sliderImages.length;
    setZoomedImage(sliderImages[prevIndex]);
  };

  const handleZoomNext = (e) => {
    e.stopPropagation();
    const currentIndex = sliderImages.indexOf(zoomedImage);
    const nextIndex = (currentIndex + 1) % sliderImages.length;
    setZoomedImage(sliderImages[nextIndex]);
  };

  // ✅ Fixed Zoom Modal Component
  const ZoomModal = () => (
    <Dialog
      open={zoomOpen}
      onClose={handleZoomClose}
      maxWidth={false}
      fullScreen
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(56, 54, 54, 0.98)',
          m: 0,
          borderRadius: 0
        }
      }}
      BackdropProps={{
        sx: {
        }
      }}
      sx={{
        zIndex: 9999, // ✅ Higher than all headers
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleZoomClose}
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 24 },
          top: { xs: 12, sm: 24 },
          zIndex: 10000,
          bgcolor: alpha(theme.palette.error.main, 0.9),
          color: 'white',
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          '&:hover': {
            bgcolor: theme.palette.error.dark,
            transform: 'scale(1.1)',
            boxShadow: '0 8px 24px rgba(211, 47, 47, 0.4)'
          },
          transition: 'all 0.3s',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
      </IconButton>

      {/* Image Counter */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 16, sm: 32 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          bgcolor: alpha(theme.palette.primary.main, 0.95),
          color: 'white',
          px: { xs: 3, sm: 4 },
          py: { xs: 1, sm: 1.5 },
          borderRadius: 4,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        }}
      >
        <Typography
          variant="body1"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}
        >
          {sliderImages.indexOf(zoomedImage) + 1} / {sliderImages.length}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          height: '100vh',
          width: '100vw'
        }}
        onClick={handleZoomClose}
      >
        {/* Previous Button */}
        <IconButton
          onClick={handleZoomPrev}
          sx={{
            position: 'fixed',
            left: { xs: 12, sm: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10000,
            bgcolor: alpha(theme.palette.common.white, 0.95),
            color: theme.palette.primary.main,
            width: { xs: 48, sm: 64 },
            height: { xs: 48, sm: 64 },
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
            },
            transition: 'all 0.3s',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: { xs: 24, sm: 32 }, ml: 1 }} />
        </IconButton>

        {/* Zoomed Image Container - 5:4 Aspect Ratio */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            p: { xs: 2, sm: 4 }
          }}
        >
          <Box
            component="img"
            src={zoomedImage}
            alt="Zoomed Driver App"
            sx={{
              maxWidth: '95vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              aspectRatio: '5/4',
              borderRadius: { xs: 1, sm: 2 },
              boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              userSelect: 'none',
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Next Button */}
        <IconButton
          onClick={handleZoomNext}
          sx={{
            position: 'fixed',
            right: { xs: 12, sm: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10000,
            bgcolor: alpha(theme.palette.common.white, 0.95),
            color: theme.palette.primary.main,
            width: { xs: 48, sm: 64 },
            height: { xs: 48, sm: 64 },
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
            },
            transition: 'all 0.3s',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
        </IconButton>
      </DialogContent>

      {/* Bottom Navigation Dots */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 24, sm: 40 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          zIndex: 10000,
          bgcolor: alpha(theme.palette.common.black, 0.7),
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderRadius: 5,
          backdropFilter: 'blur(10px)'
        }}
      >
        {sliderImages.map((img, index) => (
          <Box
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              setZoomedImage(img);
            }}
            sx={{
              width: zoomedImage === img ? { xs: 36, sm: 48 } : { xs: 12, sm: 14 },
              height: { xs: 12, sm: 14 },
              borderRadius: 7,
              bgcolor: zoomedImage === img
                ? theme.palette.primary.main
                : alpha(theme.palette.common.white, 0.5),
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: zoomedImage === img ? `3px solid ${theme.palette.common.white}` : 'none',
              '&:hover': {
                bgcolor: zoomedImage === img
                  ? theme.palette.primary.light
                  : alpha(theme.palette.common.white, 0.8),
                transform: 'scale(1.2)'
              }
            }}
          />
        ))}
      </Box>
    </Dialog>
  );

  // ✅ Image Slider Component with 5:4 Aspect Ratio
  const ImageSlider = () => (
    <Card 
      elevation={6} 
      sx={{ 
        borderRadius: { xs: 2, sm: 3 },
        overflow: 'hidden',
        position: 'relative',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        height: isMobile ? 400 : isTablet ? 520 : 900,
        width: isMobile ? 300 : isTablet ? 400 : 1100,
      }}
    >
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: { xs: 1.5, sm: 2 },
        textAlign: 'center'
      }}>
        <Typography 
          variant="h6"
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          📱 {t('driverAppPreview') || 'Driver App Preview'}
        </Typography>
      </Box>

      {/* Main Slider Container - 5:4 Aspect Ratio */}
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          height: isMobile ? 280 : isTablet ? 400 : 780,
          aspectRatio: '5/4',
          bgcolor: alpha(theme.palette.grey[200], 0.3)
        }}
      >
        {/* Image Display */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            p: { xs: 2, sm: 3 }
          }}
        >
          {sliderImages.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: currentSlide === index ? 1 : 0,
                transform: `translateX(${currentSlide === index ? 0 : currentSlide > index ? '-100%' : '100%'})`,
                transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: currentSlide === index ? 'none' : 'blur(4px)'
              }}
            >
              <Box
                component="img"
                src={image}
                alt={`Driver App Screenshot ${index + 1}`}
                onClick={() => handleZoomOpen(image)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  aspectRatio: '5/4',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  boxShadow: currentSlide === index ? '0 10px 40px rgba(0,0,0,0.15)' : 'none',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              />
              {/* Zoom Overlay */}
              {currentSlide === index && (
                <Box
                  onClick={() => handleZoomOpen(image)}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: alpha(theme.palette.common.black, 0.6),
                    color: 'white',
                    borderRadius: '50%',
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.9)
                    }
                  }}
                >
                  <ZoomInIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Navigation Arrows */}
        <IconButton
          onClick={handlePrevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 8, sm: 16 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.common.white, 0.9),
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)'
            },
            boxShadow: 3,
            zIndex: 2,
            transition: 'all 0.3s',
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: { xs: 16, sm: 20 }, ml: 0.5 }} />
        </IconButton>

        <IconButton
          onClick={handleNextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 16 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.common.white, 0.9),
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)'
            },
            boxShadow: 3,
            zIndex: 2,
            transition: 'all 0.3s',
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 }
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
        </IconButton>

        {/* Fullscreen/Zoom Button */}
        <Tooltip title="Click to enlarge" arrow placement="top">
          <IconButton
            onClick={() => handleZoomOpen(sliderImages[currentSlide])}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              right: { xs: 8, sm: 12 },
              bgcolor: alpha(theme.palette.primary.main, 0.9),
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                transform: 'scale(1.1)'
              },
              boxShadow: 2,
              zIndex: 2,
              transition: 'all 0.3s',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 }
            }}
            size="small"
          >
            <FullscreenIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
          </IconButton>
        </Tooltip>

        {/* Slide Indicators */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 12, sm: 16 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
            bgcolor: alpha(theme.palette.common.black, 0.3),
            px: 2,
            py: 1,
            borderRadius: 3,
            backdropFilter: 'blur(10px)'
          }}
        >
          {sliderImages.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: currentSlide === index ? 32 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: currentSlide === index 
                  ? theme.palette.primary.main 
                  : alpha(theme.palette.common.white, 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: currentSlide === index ? 2 : 0,
                border: currentSlide === index ? `2px solid ${theme.palette.common.white}` : 'none',
                '&:hover': {
                  bgcolor: currentSlide === index 
                    ? theme.palette.primary.light 
                    : alpha(theme.palette.common.white, 0.8),
                  transform: 'scale(1.2)'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Slide Info */}
      <Box sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            fontWeight: 500
          }}
        >
          📸 {currentSlide + 1} / {sliderImages.length}
        </Typography>
        <Button
          size="small"
          startIcon={<ZoomInIcon />}
          onClick={() => handleZoomOpen(sliderImages[currentSlide])}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            textTransform: 'none'
          }}
        >
          اضغط لتكبير الصورة
        </Button>
      </Box>
    </Card>
  );

  // ✅ Mobile-Optimized Help Dialog
  const HelpDialog = () => {
    const steps = [
      {
        label: t('helpDriversRegisterStep1Label') || 'Step 1: Driver Information',
        icon: <PersonIcon color="primary" />,
        description: t('helpDriversRegisterStep1Description') || 'Enter basic driver information',
        tips: [
          t('helpDriversRegisterStep1Tip1') || 'Full name is required',
          t('helpDriversRegisterStep1Tip2') || 'Use driver\'s real name',
          t('helpDriversRegisterStep1Tip3') || 'Email is optional'
        ]
      },
      {
        label: t('helpDriversRegisterStep2Label') || 'Step 2: Contact & Location',
        icon: <LocationOnIcon color="primary" />,
        description: t('helpDriversRegisterStep2Description') || 'Add contact and location details',
        tips: [
          t('helpDriversRegisterStep2Tip1') || 'Mobile number must be 9 digits',
          t('helpDriversRegisterStep2Tip2') || 'Select driver\'s city'
        ]
      },
      {
        label: t('helpDriversRegisterStep3Label') || 'Step 3: Set Password',
        icon: <LockIcon color="primary" />,
        description: t('helpDriversRegisterStep3Description') || 'Create secure password for driver login',
        tips: [
          t('helpDriversRegisterStep3Tip1') || 'Use strong password',
          t('helpDriversRegisterStep3Tip2') || 'Driver will use this to login'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
        sx={{
          zIndex: 9998 // ✅ Below zoom modal
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('helpDriversRegisterDialogTitle') || 'Driver Registration Help'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setHelpOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              {t('helpWhatIsDriverRegister') || 'Driver Registration'}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('helpWhatIsDriverRegisterDesc') || 'Register new drivers to deliver orders for your branch.'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      borderRadius: '50%', 
                      p: { xs: 1, sm: 1.5 }, 
                      display: 'flex',
                      boxShadow: 1
                    }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ 
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1.1rem' }
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold" 
                            color="primary" 
                            sx={{ 
                              mb: 1, 
                              display: 'block',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }
                            }}
                          >
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ 
                                fontSize: { xs: 14, sm: 16 }, 
                                mr: 1, 
                                color: 'success.main', 
                                mt: 0.3 
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                              >
                                {tip}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              <Box sx={{ mt: 1 }}>
                • {t('helpDriversRegisterImportantNote1') || 'Mobile number must be unique'}<br/>
                • {t('helpDriversRegisterImportantNote2') || 'Driver will use mobile to login'}<br/>
                • {t('helpDriversRegisterImportantNote3') || 'Password must be secure'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setHelpOpen(false)} 
            variant="contained" 
            color="primary" 
            fullWidth={isMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />
      <ZoomModal />

      {/* ✅ Mobile-Optimized Header */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <PersonIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            {t('registerNewDriver') || 'Register New Driver'}
          </Typography>
        </Stack>
        <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
          <Button
            variant="contained"
            color="info"
            startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={() => setHelpOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {t('helpButtonText') || 'Help'}
          </Button>
        </Tooltip>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('pleaseSelectBranchRegisterDriver') || 'Please select a branch first'}
        </Alert>
      )}

      {/* ✅ Two Column Layout: Form + Slider */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Left Column - Registration Form */}
        <Grid item xs={12} lg={7}>
          <Card elevation={3} sx={{ borderRadius: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              bgcolor: 'primary.main', 
              color: 'white', 
              p: { xs: 2, sm: 2.5 }
            }}>
              <Typography 
                variant="h6"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                {t('registerNewDriver') || 'Register New Driver'}
              </Typography>
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                {t('fillDriverInformation') || 'Fill in the driver information below'}
              </Typography>
            </Box>
            
            <form onSubmit={handleSubmit}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {/* Full Name */}
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {t('fullNameLabel') || 'Full Name'} *
                    </Typography>
                    <TextField
                      fullWidth
                      name="FullName"
                      placeholder={t('enterFullName') || 'Enter full name'}
                      value={formData.FullName}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </InputAdornment>
                        ),
                      }}
                      helperText={t('pleaseEnterFullName') || 'Driver\'s full name'}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                  </Grid>

                  {/* Mobile Number */}
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {t('mobileNumberLabel') || 'Mobile Number'} *
                    </Typography>
                    <TextField
                      fullWidth
                      name="MobileNumber"
                      placeholder="777222333"
                      value={formData.MobileNumber}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            <Typography sx={{ 
                              ml: 1, 
                              fontWeight: 'bold', 
                              color: 'text.secondary',
                              fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }}>
                              +967
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      helperText={t('pleaseEnterValidMobileNumber') || '9 digits without country code'}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {t('emailAddressLabel') || 'Email Address'}
                    </Typography>
                    <TextField
                      fullWidth
                      name="Email"
                      type="email"
                      placeholder={t('enterEmail') || 'email@example.com'}
                      value={formData.Email}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </InputAdornment>
                        ),
                      }}
                      helperText={t('emailShareDisclaimer') || 'Optional - used for notifications'}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                  </Grid>

                  {/* City */}
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {t('cityLabel') || 'City'} *
                    </Typography>
                    <FormControl fullWidth required size={isMobile ? "small" : "medium"}>
                      <Select
                        name="City"
                        value={formData.City}
                        onChange={handleChange}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <LocationOnIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </InputAdornment>
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('selectCity') || 'Select city'}</em>
                        </MenuItem>
                        {cities.map((city) => (
                          <MenuItem 
                            key={city.id || city.Id || city.name || city.Name} 
                            value={city.name || city.Name}
                            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                          >
                            {city.name || city.Name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {t('pleaseSelectCity') || 'Driver\'s working city'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Password */}
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {t('passwordLabel') || 'Password'} *
                    </Typography>
                    <TextField
                      fullWidth
                      name="Password"
                      type="password"
                      placeholder={t('enterPassword') || 'Enter password'}
                      value={formData.Password}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </InputAdornment>
                        ),
                      }}
                      helperText={t('pleaseEnterStrongPassword') || 'Used for driver app login'}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>

              {/* ✅ Mobile-Optimized Action Buttons */}
              <Box sx={{ 
                p: { xs: 2, sm: 2.5 }, 
                borderTop: 1, 
                borderColor: 'divider', 
                bgcolor: '#f9f9f9' 
              }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={{ xs: 1.5, sm: 2 }}
                >
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="success"
                    size={isMobile ? "medium" : "large"}
                    startIcon={<SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    fullWidth={isMobile}
                    sx={{ 
                      px: { xs: 3, sm: 4 }, 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6
                      }
                    }}
                  >
                    {t('registerDriver') || 'Register Driver'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outlined" 
                    color="error" 
                    size={isMobile ? "medium" : "large"}
                    onClick={handleReset}
                    fullWidth={isMobile}
                    sx={{ 
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}
                  >
                    {t('cancel') || 'Cancel'}
                  </Button>
                </Stack>
              </Box>
            </form>
          </Card>
        </Grid>

        {/* ✅ Right Column - Image Slider */}
        <Grid item xs={12} lg={5}>
          <ImageSlider />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RegisterDriver;